/* SNOW — theme.js */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const html = document.documentElement;
  const S = window.SNOW || {};
  const routes = S.routes || {};
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches && html.classList.contains('rm-respect');
  const motion = html.classList.contains('motion') && !reduced;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const parse = (text) => new DOMParser().parseFromString(text, 'text/html');
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    format = format || S.moneyFormat || '{{amount_with_comma_separator}} €';
    const n = Number(cents) || 0;
    const fmt = (prec, thou, dec) => {
      const [i, d] = (n / 100).toFixed(prec).split('.');
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return d ? int + dec + d : int;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(0, ',', '.');
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        default: return fmt(2, ',', '.');
      }
    });
  }

  /* ---------- Toast ---------- */
  let toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3800);
  }

  /* ---------- Reveal ---------- */
  const io = 'IntersectionObserver' in window && motion && !designMode
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 })
    : null;

  function observeReveal(scope = document) {
    $$('[data-reveal]:not(.is-in)', scope).forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  }

  function heroIn(scope = document) {
    $$('[data-hero]', scope).forEach((h) => {
      requestAnimationFrame(() => requestAnimationFrame(() => h.classList.add('is-in')));
    });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const wrap = $('.section-header');
    if (!wrap) return;
    let last = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      wrap.classList.toggle('is-scrolled', y > 8);
      if (!html.classList.contains('lock')) {
        const down = y > last + 2;
        const up = y < last - 2;
        if (down && y > 260) wrap.classList.add('is-hidden');
        else if (up || y < 260) wrap.classList.remove('is-hidden');
      }
      last = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- Drawers ---------- */
  const overlay = $('[data-overlay]');
  let current = null;
  let lastFocus = null;

  function openDrawer(name) {
    const d = $(`[data-drawer="${name}"]`);
    if (!d) return false;
    if (name === 'facets' && isDesktop()) return false;
    if (current && current !== d) closeDrawer(false);
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-visible');
    html.classList.add('lock');
    current = d;
    $$(`[aria-controls="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    if (name === 'cart') loadUpsell(d);
    setTimeout(() => {
      const target = $('[data-autofocus]', d) || d;
      target.focus({ preventScroll: true });
    }, 60);
    return true;
  }

  function closeDrawer(restore = true) {
    if (!current) return;
    const d = current;
    d.classList.remove('is-open');
    if (!(d.id === 'Facets' && isDesktop())) d.setAttribute('aria-hidden', 'true');
    $$(`[aria-controls="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    current = null;
    if (overlay) overlay.classList.remove('is-visible');
    html.classList.remove('lock');
    if (restore && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      if (openDrawer(opener.dataset.open)) e.preventDefault();
      return;
    }
    if (e.target.closest('[data-close]') || e.target === overlay) closeDrawer();
  });

  document.addEventListener('keydown', (e) => {
    if (!current) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    const focusables = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', current)
      .filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  window.addEventListener('resize', () => {
    if (current && current.id === 'Facets' && isDesktop()) closeDrawer(false);
  });

  /* ---------- Cart ---------- */
  const Cart = {
    enabled() { return !!(S.cartDrawer && $('[data-drawer="cart"]')); },

    sectionIds() {
      const ids = ['cart-drawer'];
      $$('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
      return Array.from(new Set(ids));
    },

    async add(form, button) {
      const fd = new FormData(form);
      fd.append('sections', this.sectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      if (button) button.classList.add('is-loading');
      const err = $('[data-form-error]', form.closest('[data-product]') || form);
      if (err) err.hidden = true;
      try {
        const res = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          const msg = data.description || data.message || "Impossible d'ajouter cet article.";
          if (err) { err.textContent = msg; err.hidden = false; } else toast(msg);
          return;
        }
        this.render(data.sections);
        if (button && button.classList.contains('card__quick-btn')) {
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 1600);
        }
        if (current && current.id === 'CartDrawer') return;
        await flyToCart(form);
        openDrawer('cart');
      } catch (e) {
        form.submit();
      } finally {
        if (button) button.classList.remove('is-loading');
      }
    },

    async change(line, quantity, row) {
      if (row) row.classList.add('is-updating');
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          toast(data.description || data.message || 'Quantité indisponible.');
          if (row) row.classList.remove('is-updating');
          return;
        }
        this.render(data.sections);
      } catch (e) {
        window.location.reload();
      }
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, markup]) => {
        if (!markup) return;
        const doc = parse(markup);
        if (id === 'cart-drawer') {
          const fresh = $('[data-cart-inner]', doc);
          const cur = $('[data-cart-inner]');
          if (!fresh || !cur) return;
          const oldBar = $('.ship-bar__track span', cur);
          const oldWidth = oldBar ? oldBar.style.width : null;
          cur.replaceWith(fresh);
          const newBar = $('.ship-bar__track span', fresh);
          if (newBar && oldWidth !== null) {
            const target = newBar.style.width;
            newBar.style.width = oldWidth;
            requestAnimationFrame(() => requestAnimationFrame(() => { newBar.style.width = target; }));
          }
          this.updateCount(Number(fresh.dataset.cartCount || 0));
          loadUpsell(fresh);
        } else {
          const cur = document.getElementById(`shopify-section-${id}`);
          const fresh = doc.getElementById(`shopify-section-${id}`);
          if (cur && fresh) cur.innerHTML = fresh.innerHTML;
        }
      });
      observeReveal();
    },

    updateCount(count) {
      $$('[data-cart-bubble]').forEach((b) => {
        const changed = b.textContent.trim() !== String(count);
        b.textContent = count;
        b.classList.toggle('is-empty', count === 0);
        if (changed && count > 0) {
          b.classList.remove('is-bump');
          void b.offsetWidth;
          b.classList.add('is-bump');
        }
      });
    },
  };

  /* ---------- Fly to cart ---------- */
  function visibleImage(scope) {
    if (!scope) return null;
    return $$('img', scope).find((img) => {
      const r = img.getBoundingClientRect();
      return r.width > 20 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    }) || null;
  }

  function flyToCart(form) {
    const scope = form.closest('.card, .upsell__item, .tt, [data-product]');
    let img = visibleImage(scope);
    if (!img && scope && scope.matches('[data-product]')) img = visibleImage($('[data-sticky-atc]', scope));
    const headerWrap = $('.section-header');
    if (headerWrap) headerWrap.classList.remove('is-hidden');
    const target = $('.header [data-open="cart"]');
    if (!motion || !img || !target || !img.animate) return Promise.resolve();
    const a = img.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const clone = img.cloneNode(false);
    clone.removeAttribute('srcset');
    clone.removeAttribute('sizes');
    clone.removeAttribute('loading');
    Object.assign(clone.style, {
      position: 'fixed', left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px`,
      zIndex: 90, objectFit: 'cover', borderRadius: '10px', pointerEvents: 'none', margin: 0, opacity: 1, transform: 'none', scale: 'none',
      boxShadow: '0 20px 40px rgba(0,0,0,.25)',
    });
    document.body.appendChild(clone);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const anim = clone.animate([
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.55 - 70}px) scale(.42)`, opacity: 0.95, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(.06)`, opacity: 0.3 },
    ], { duration: 780, easing: 'cubic-bezier(.65,0,.35,1)' });
    return anim.finished.catch(() => {}).then(() => {
      clone.remove();
      const bubble = $('.header [data-cart-bubble]');
      if (bubble) { bubble.classList.remove('is-bump'); void bubble.offsetWidth; bubble.classList.add('is-bump'); }
    });
  }

  /* ---------- Cart upsell ---------- */
  async function loadUpsell(scope = document) {
    const slot = $('[data-upsell]', scope);
    if (!slot || slot.dataset.loaded || !slot.dataset.url || slot.dataset.url.endsWith('product_id=')) return;
    slot.dataset.loaded = '1';
    try {
      const res = await fetch(slot.dataset.url);
      if (!res.ok) return;
      const doc = parse(await res.text());
      const inner = $('[data-upsell-inner]', doc);
      if (inner && slot.isConnected) slot.innerHTML = inner.innerHTML;
    } catch (e) { /* no suggestions */ }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form.js-product-form');
    if (!form || !Cart.enabled()) return;
    e.preventDefault();
    const button = e.submitter || $('[type="submit"]', form);
    Cart.add(form, button);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    e.preventDefault();
    Cart.change(Number(btn.dataset.line), Number(btn.dataset.quantity), btn.closest('.citem'));
  });

  /* ---------- Product ---------- */
  function goToMedia(root, id) {
    const track = $('[data-gallery-track]', root);
    const slide = track && $(`[data-media-id="${id}"]`, track);
    if (!slide) return;
    if (track.scrollWidth > track.clientWidth + 2) {
      track.scrollTo({ left: slide.offsetLeft, behavior: motion ? 'smooth' : 'auto' });
    }
  }

  function initGallery(root) {
    const track = $('[data-gallery-track]', root);
    if (!track) return;
    const counter = $('[data-gallery-index]', root);
    const dots = $$('.gallery__dots span', root);
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (counter) counter.textContent = i + 1;
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      });
    }, { passive: true });
  }

  function initSticky(root) {
    const bar = $('[data-sticky-atc]', root);
    const atc = $('[data-atc]', root);
    if (!bar || !atc || !('IntersectionObserver' in window)) return;
    bar.inert = true;
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
    }).observe(atc);
  }

  function stockState(variant, stock) {
    if (!variant) return { cls: 'stock--out', text: 'Combinaison indisponible' };
    if (!variant.available) return { cls: 'stock--out', text: 'Vendu' };
    const qty = stock[variant.id];
    if (qty === 1) return { cls: 'stock--low', text: 'Pièce unique — dernière disponible' };
    if (typeof qty === 'number' && qty > 1 && qty <= 3) return { cls: 'stock--low', text: `Plus que ${qty} en stock` };
    return { cls: 'stock--ok', text: 'En stock, prêt à être expédié' };
  }

  function initProduct(scope = document) {
    $$('[data-product]', scope).forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const jsonEl = $('[data-product-json]', root);
      if (!jsonEl) return;
      const product = JSON.parse(jsonEl.textContent);
      const stockEl = $('[data-variant-stock]', root);
      const stock = stockEl ? JSON.parse(stockEl.textContent) : {};
      const form = $('form.js-product-form', root);
      const idInput = form && $('input[name="id"]', form);
      const picker = $('[data-variant-picker]', root);
      const groups = picker ? $$('[data-option-group]', picker) : [];

      const selected = () => groups.map((g) => {
        const c = $('input:checked', g);
        return c ? c.value : null;
      });
      const find = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

      const refreshAvailability = (opts) => {
        groups.forEach((g, gi) => {
          $$('input', g).forEach((input) => {
            const test = opts.slice();
            test[gi] = input.value;
            const v = find(test);
            const label = root.querySelector(`label[for="${input.id}"]`);
            if (label) label.classList.toggle('is-unavailable', !v || !v.available);
          });
        });
      };

      const update = (variant, opts) => {
        if (idInput) idInput.value = variant ? variant.id : '';
        $$('[data-option-label]', root).forEach((el) => { el.textContent = opts[Number(el.dataset.optionLabel)] || ''; });

        if (variant) {
          $$('[data-price]', root).forEach((el) => { el.textContent = formatMoney(variant.price); });
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          const priceWrap = $('[data-price-wrap]', root);
          if (priceWrap) priceWrap.classList.toggle('price--sale', !!onSale);
          const cmp = $('[data-compare]', root);
          if (cmp) { cmp.hidden = !onSale; if (onSale) cmp.textContent = formatMoney(variant.compare_at_price); }
          const save = $('[data-save]', root);
          if (save) {
            save.hidden = !onSale;
            if (onSale) save.textContent = `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)} %`;
          }
        }

        const available = !!(variant && variant.available);
        $$('[data-atc], [data-sticky-btn]', root).forEach((b) => { b.disabled = !available; });
        $$('[data-atc-label]', root).forEach((el) => {
          el.textContent = !variant ? 'Indisponible' : available ? (el.dataset.label || 'Ajouter au panier') : 'Vendu';
        });
        $$('[data-atc-price]', root).forEach((el) => { el.textContent = variant && available ? formatMoney(variant.price) : ''; });
        $$('[data-atc-sep]', root).forEach((el) => { el.hidden = !available; });

        const st = $('[data-stock]', root);
        if (st) {
          const s = stockState(variant, stock);
          st.className = `stock ${s.cls}`;
          $('[data-stock-text]', st).textContent = s.text;
        }

        if (variant && variant.featured_media) goToMedia(root, variant.featured_media.id);
        if (variant && !designMode) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        refreshAvailability(opts);
      };

      if (picker) {
        picker.addEventListener('change', () => {
          const opts = selected();
          update(find(opts), opts);
        });
        refreshAvailability(selected());
      }
      initGallery(root);
      initSticky(root);
    });
  }

  /* ---------- Delivery estimate ---------- */
  function initEta(scope = document) {
    $$('[data-eta]', scope).forEach((el) => {
      const addBusinessDays = (n) => {
        const d = new Date();
        let k = 0;
        while (k < n) {
          d.setDate(d.getDate() + 1);
          const w = d.getDay();
          if (w !== 0 && w !== 6) k += 1;
        }
        return d;
      };
      const min = Number(el.dataset.min) || 2;
      const max = Math.max(Number(el.dataset.max) || 4, min);
      try {
        const f = new Intl.DateTimeFormat(S.locale || 'fr', { weekday: 'short', day: 'numeric', month: 'short' });
        el.textContent = `Livraison estimée : ${f.format(addBusinessDays(min))} – ${f.format(addBusinessDays(max))}`;
      } catch (e) { /* keep server text */ }
    });
  }

  /* ---------- Facets ---------- */
  function initFacets(scope = document) {
    const form = scope.id === 'Facets' ? scope : $('#Facets', scope);
    if (!form || form.dataset.ready) return;
    form.dataset.ready = '1';
    const section = form.closest('[data-section-id]');
    if (!section) return;
    const sid = section.dataset.sectionId;
    let timer;
    let ctrl;

    const urlFromForm = () => {
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form)) {
        if (String(v).trim() !== '') params.append(k, v);
      }
      const q = params.toString();
      return window.location.pathname + (q ? `?${q}` : '');
    };

    const render = async (url) => {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      section.classList.add('is-loading');
      const activeId = document.activeElement && document.activeElement.id;
      const openStates = {};
      $$('details.facet', form).forEach((d) => { openStates[d.id] = d.open; });
      try {
        const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${sid}`, { signal: ctrl.signal });
        const doc = parse(await res.text());
        ['[data-facets-body]', '[data-results]', '[data-facets-footer]'].forEach((sel) => {
          const fresh = $(sel, doc);
          const cur = $(sel, section);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        const freshCounts = $$('[data-facets-count]', doc);
        $$('[data-facets-count]', section).forEach((el, i) => { if (freshCounts[i]) el.innerHTML = freshCounts[i].innerHTML; });
        Object.entries(openStates).forEach(([id, open]) => {
          const d = document.getElementById(id);
          if (d) d.open = open;
        });
        if (activeId) {
          const el = document.getElementById(activeId);
          if (el && form.contains(el)) {
            el.focus({ preventScroll: true });
            if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
          }
        }
        window.history.replaceState({}, '', url);
        observeReveal(section);
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url;
      } finally {
        section.classList.remove('is-loading');
      }
    };

    section.addEventListener('input', (e) => {
      if (e.target.form !== form) return;
      clearTimeout(timer);
      timer = setTimeout(() => render(urlFromForm()), e.target.type === 'number' ? 650 : 120);
    });
    form.addEventListener('submit', (e) => { e.preventDefault(); render(urlFromForm()); });
    section.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-facet-link]');
      if (!a) return;
      e.preventDefault();
      render(a.getAttribute('href'));
    });
  }

  /* ---------- Load more ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();
    const section = btn.closest('[data-section-id]');
    const grid = section && $('[data-grid]', section);
    if (!grid) { window.location.href = btn.href; return; }
    btn.classList.add('is-loading');
    try {
      const url = btn.getAttribute('href');
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${section.dataset.sectionId}`);
      const doc = parse(await res.text());
      $$('[data-grid] > *', doc).forEach((item, i) => {
        item.style.setProperty('--i', i % 4);
        grid.appendChild(item);
      });
      const next = $('[data-load-more]', doc);
      const wrap = btn.closest('.load-more');
      if (next) btn.replaceWith(next);
      else if (wrap) wrap.remove();
      observeReveal(grid);
    } catch (err) {
      window.location.href = btn.href;
    }
  });

  /* ---------- Recommendations ---------- */
  function initRecommendations(scope = document) {
    $$('[data-recommendations]', scope).forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      try {
        const res = await fetch(el.dataset.url);
        const doc = parse(await res.text());
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          observeReveal(el);
          initRails(el);
        }
      } catch (e) { /* silent */ }
    });
  }

  /* ---------- Rails ---------- */
  function initRails(scope = document) {
    $$('[data-rail-wrap]', scope).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      const rail = $('[data-rail]', wrap);
      if (!rail) return;
      wrap.dataset.ready = '1';
      const prev = $('[data-rail-prev]', wrap);
      const next = $('[data-rail-next]', wrap);
      const step = () => rail.clientWidth * 0.8;
      const sync = () => {
        if (prev) prev.disabled = rail.scrollLeft < 4;
        if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      };
      if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (!motion) return;
    let ticking = false;
    const run = () => {
      const vh = window.innerHeight;
      $$('[data-parallax]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const speed = parseFloat(el.dataset.parallax) || 0.1;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(run); }
    }, { passive: true });
    run();
  }

  /* ---------- Announcement rotator ---------- */
  function initRotators(scope = document) {
    $$('[data-rotator]', scope).forEach((r) => {
      if (r.dataset.ready) return;
      r.dataset.ready = '1';
      const items = Array.from(r.children);
      if (items.length < 2) return;
      let i = 0;
      setInterval(() => {
        if (isDesktop() || document.hidden) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4200);
    });
  }

  /* ---------- Snow ---------- */
  function initSnow(scope = document) {
    if (!motion) return;
    $$('canvas[data-snow]', scope).forEach((cv) => {
      if (cv.dataset.ready) return;
      cv.dataset.ready = '1';
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      let w = 0, h = 0, flakes = [], raf = null, visible = true;
      const make = (init) => ({
        x: Math.random() * w, y: init ? Math.random() * h : -6,
        r: Math.random() * 2 + 0.5, s: Math.random() * 0.5 + 0.25,
        o: Math.random() * 0.5 + 0.2, p: Math.random() * Math.PI * 2, d: Math.random() * 0.6 - 0.3,
      });
      const resize = () => {
        const nw = cv.clientWidth, nh = cv.clientHeight;
        if (nw === w && Math.abs(nh - h) < 120) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        w = nw; h = nh;
        cv.width = w * dpr; cv.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const n = Math.round(Math.min(120, (w * h) / 8000) * (parseFloat(cv.dataset.density) || 1));
        flakes = Array.from({ length: n }, () => make(true));
      };
      const tick = () => {
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = '#fff';
        for (const f of flakes) {
          f.p += 0.012;
          f.y += f.s * (f.r * 0.5 + 0.5);
          f.x += Math.sin(f.p) * 0.35 + f.d * 0.15;
          if (f.y > h + 6 || f.x < -6 || f.x > w + 6) Object.assign(f, make(false));
          ctx.globalAlpha = f.o;
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
          ctx.fill();
        }
        raf = visible && !document.hidden ? requestAnimationFrame(tick) : null;
      };
      const start = () => { if (!raf && visible) raf = requestAnimationFrame(tick); };
      resize();
      window.addEventListener('resize', resize);
      document.addEventListener('visibilitychange', start);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => { visible = e.isIntersecting; start(); }).observe(cv);
      }
      start();
    });
  }

  /* ---------- Rotating words ---------- */
  function initWords(scope = document) {
    $$('[data-rotate-words]', scope).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const words = $$('.hero2__word', el);
      if (words.length < 2 || reduced) return;
      let i = 0;
      setInterval(() => {
        if (document.hidden) return;
        const cur = words[i];
        i = (i + 1) % words.length;
        const next = words[i];
        cur.classList.remove('is-active');
        cur.classList.add('is-out');
        next.classList.add('is-active');
        setTimeout(() => {
          cur.style.transition = 'none';
          cur.classList.remove('is-out');
          void cur.offsetWidth;
          cur.style.transition = '';
        }, 1000);
      }, 2600);
    });
  }

  /* ---------- Counters ---------- */
  function initCounters(scope = document) {
    if (!motion || !('IntersectionObserver' in window)) return;
    $$('[data-count-to]', scope).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const target = Number(el.dataset.countTo) || 0;
      const obs = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        obs.disconnect();
        const t0 = performance.now();
        const dur = 1600;
        const step = (t) => {
          const k = Math.min((t - t0) / dur, 1);
          el.textContent = Math.round(target * (1 - Math.pow(1 - k, 4)));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.4 });
      obs.observe(el);
    });
  }

  /* ---------- Spotlight ---------- */
  document.addEventListener('pointermove', (e) => {
    const el = e.target.closest && e.target.closest('[data-spotlight]');
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  }, { passive: true });

  /* ---------- Hero scroll-out ---------- */
  function initHeroScroll() {
    if (!motion) return;
    const content = $('[data-hero-content]');
    if (!content) return;
    const hero = content.closest('[data-hero]');
    let ticking = false;
    const run = () => {
      const p = Math.min(Math.max(window.scrollY / (hero.offsetHeight * 0.8), 0), 1);
      content.style.transform = `translate3d(0, ${(p * 70).toFixed(1)}px, 0)`;
      content.style.opacity = String(1 - p * 0.9);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(run); }
    }, { passive: true });
    run();
  }

  /* ---------- Headings: word reveal ---------- */
  function initSplit(scope = document) {
    if (!motion || designMode) return;
    $$('.h-section:not(.is-split)', scope).forEach((el) => {
      if (el.children.length) return;
      const words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach((word, i) => {
        const outer = document.createElement('span');
        outer.className = 'w';
        const inner = document.createElement('span');
        inner.textContent = word;
        inner.style.setProperty('--wi', i);
        outer.appendChild(inner);
        el.appendChild(outer);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.classList.add('is-split');
      if (!el.closest('[data-reveal]')) { el.setAttribute('data-reveal', ''); observeReveal(el.parentElement || el); }
    });
  }

  /* ---------- Videos: play only when visible ---------- */
  const videoIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
        });
      }, { threshold: 0.15 })
    : null;
  function initVideos(scope = document) {
    $$('video.tt__video', scope).forEach((v) => {
      if (v.dataset.ready) return;
      v.dataset.ready = '1';
      v.muted = true;
      v.setAttribute('muted', '');
      if (videoIO) videoIO.observe(v);
    });
  }

  /* ---------- TikTok thumbnails (oEmbed) ---------- */
  const tiktokThumbCache = new Map();
  function initTiktokThumbs(scope = document) {
    $$('[data-tiktok-thumb]', scope).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const url = el.dataset.tiktokThumb;
      if (!url) return;
      const apply = (thumb) => {
        if (!thumb) return;
        const img = document.createElement('img');
        img.src = thumb;
        img.alt = '';
        img.loading = 'lazy';
        el.prepend(img);
        el.classList.add('is-loaded');
      };
      if (tiktokThumbCache.has(url)) { apply(tiktokThumbCache.get(url)); return; }
      fetch('https://www.tiktok.com/oembed?url=' + encodeURIComponent(url))
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const thumb = data && data.thumbnail_url;
          tiktokThumbCache.set(url, thumb || null);
          apply(thumb);
        })
        .catch(() => { tiktokThumbCache.set(url, null); });
    });
  }

  /* ---------- Marquee: pause while touched ---------- */
  document.addEventListener('pointerdown', (e) => {
    const m = e.target.closest && e.target.closest('[data-marquee]');
    if (!m) return;
    m.classList.add('is-paused');
    const resume = () => setTimeout(() => m.classList.remove('is-paused'), 1800);
    document.addEventListener('pointerup', resume, { once: true });
    document.addEventListener('pointercancel', resume, { once: true });
  }, { passive: true });

  /* ---------- Magnetic buttons (desktop) ---------- */
  function initMagnetic(scope = document) {
    if (!motion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    $$('[data-magnetic], .btn--buy', scope).forEach((el) => {
      if (el.dataset.magReady) return;
      el.dataset.magReady = '1';
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width - 0.5) * 10;
        const y = ((e.clientY - r.top) / r.height - 0.5) * 8;
        el.style.translate = `${x.toFixed(1)}px ${y.toFixed(1)}px`;
      });
      el.addEventListener('pointerleave', () => { el.style.translate = ''; });
    });
  }

  /* ---------- Init ---------- */
  function initAll(scope = document) {
    initSplit(scope);
    initVideos(scope);
    initTiktokThumbs(scope);
    initMagnetic(scope);
    observeReveal(scope);
    heroIn(scope);
    initSnow(scope);
    initWords(scope);
    initCounters(scope);
    initProduct(scope);
    initRails(scope);
    initRecommendations(scope);
    initEta(scope);
    initRotators(scope);
    initFacets(scope);
  }

  initHeader();
  initParallax();
  initHeroScroll();
  initAll();

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const rail = block.closest('[data-rail]');
    if (rail) rail.scrollTo({ left: block.offsetLeft - rail.offsetLeft, behavior: 'smooth' });
  });
})();
